import { Box, Button } from '@mui/material'
import { formatFullDate } from 'utils/util'
import { Page } from 'components/Shared/Page'
import { DataTable } from 'components/Shared/DataTable'
import { PageHeader } from 'components/Shared/PageHeader'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { type GridColDef, type GridEventListener } from '@mui/x-data-grid'
import { type ListSweepstake } from 'types/api'
import { SweepstakesStatusLabel } from 'components/Sweepstakes/SweepstakesStatusLabel'
import { GridSearchField } from 'components/GridSearchField/GridSearchField'
import { useState } from 'react'
import { DeleteSweepstakesRenderCell } from './DeleteSweepstakesRenderCell'
import { ErrorIndicator } from 'components/ErrorIndicator/ErrorIndicator'
import { NoDataIndicator } from 'components/NoDataIndicator/NoDataIndicator'
import { useGetListSweepstakes } from 'hooks/api/useGetListSweepstakes'

const columns: Array<GridColDef<ListSweepstake>> = [
  {
    field: 'name',
    headerName: 'Sweepstakes Name',
    minWidth: 360,
    flex: 1,
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 230,
    renderCell: (params) => {
      const status = params.row.sweepstakeState ?? 'Draft'
      return <SweepstakesStatusLabel status={status} />
    },
  },
  {
    field: 'startDate',
    headerName: 'Start',
    minWidth: 150,
    renderCell: (params) => <Box>{formatFullDate(params.value)}</Box>,
  },
  {
    field: 'endDate',
    headerName: 'End',
    minWidth: 150,
    renderCell: (params) => <Box>{formatFullDate(params.value)}</Box>,
  },
  {
    field: 'pinnedToTop',
    headerName: 'Pinned to Top',
    minWidth: 150,
    renderCell: (params) => params.value && <Box>Yes</Box>,
  },
  {
    field: '__menu__',
    headerName: '',
    align: 'left',
    renderCell: (params) => {
      // Only display the option menu on the row if the status column is 'draft'
      const status = params.row.sweepstakeState ?? 'Draft'
      return status === 'Draft' ? (
        <DeleteSweepstakesRenderCell params={params} />
      ) : null
    },
  },
]

export const SweepstakesPage = () => {
  const navigate = useNavigate()
  const query = useGetListSweepstakes({ type: 'Sweepstake' })
  const [filteredData, setFilteredData] = useState<ListSweepstake[]>()

  const handleFilteredData = (filteredData: ListSweepstake[]) => {
    setFilteredData(filteredData)
  }

  const selectCell: GridEventListener<'cellClick'> = (params) => {
    if (params.field !== '__menu__' && params.id !== null) {
      navigate(`/Contests/Sweepstakes/${params.id}/Overview`)
    }
  }

  return (
    <Page
      header={
        <PageHeader
          title="J&J Sweepstakes"
          eyebrow="CONTESTS"
          actionButton={
            <RouterLink to="/Contests/Sweepstakes/New" className="gap-1">
              <Button
                variant="contained"
                sx={{
                  px: '16px',
                  width: { xs: '100%', sm: '100%', md: 200, lg: 200 },
                }}
              >
                Create New Sweepstake
              </Button>
            </RouterLink>
          }
        />
      }
    >
      {query.isPending || query.isFetching ? (
        <ActivityIndicator />
      ) : query.isError ? (
        <ErrorIndicator />
      ) : !query.data ? (
        <NoDataIndicator />
      ) : (
        <>
          <GridSearchField<'ContestListDTO'>
            placeholder="Search Sweepstakes"
            data={query.data ?? []}
            handleFilteredData={handleFilteredData}
            fullWidth
          />

          <DataTable
            columns={columns}
            rows={filteredData ?? query.data}
            onCellClick={selectCell}
          />
        </>
      )}
    </Page>
  )
}
